/**
 * Public recruiting pilot demo — read-only fixture data (no auth, no LLM).
 * Mirrors IF-recruiting-pilot-v1 with previewPanelOutputs shaped for runtime getSlice.
 */

export const RECRUITING_PUBLIC_DEMO_ID = 'if-recruiting-pilot-demo';

export const recruitingPublicDemoDefinition = {
  schemaVersion: 'app/v2',
  id: RECRUITING_PUBLIC_DEMO_ID,
  name: 'Recruiting pilot — live demo',
  description: 'Structured candidate screening and post-interview scorecard with in-app phase handoff.',
  layout: 'three-panel',
  chatPosition: 'center',
  theme: { themeId: 'forest' },
  metadata: {
    interviewFlow: {
      playbook: '1+3',
      sharedChatHistoryAcrossPanels: true,
      persistSharedChatHistory: false,
      initialPhaseId: 'screening',
      phases: [
        { id: 'screening', label: 'Screening', order: 0 },
        { id: 'scorecard', label: 'Scorecard', order: 1 },
      ],
      handoff: {
        targetPhaseId: 'scorecard',
        copyFields: ['candidateName', 'roleApplied', 'screeningBucket', 'screeningScore'],
        note: 'Click a pass row in the screening queue to switch to scorecard with prefill.',
      },
      previewChatHistory: [
        {
          role: 'user',
          content: 'Screen Jordan Lee for Senior Backend Engineer — 7 years backend, payments reliability focus.',
        },
        {
          role: 'assistant',
          content: '**Jordan Lee** scored **82** — bucket **pass**.\n\n- Strong distributed systems and idempotency patterns.\n- Role summary alignment: high.\n\n**Next:** switch to **Scorecard** phase after interview feedback.',
        },
      ],
      previewPanelOutputs: {
        left_queue: {
          table_data: {
            columns: [
              { field: 'candidate', headerName: 'Candidate', width: 160 },
              { field: 'role', headerName: 'Role', width: 140 },
              { field: 'bucket', headerName: 'Bucket', width: 100 },
              { field: 'score', headerName: 'Score', width: 80 },
              { field: 'nextStep', headerName: 'Next', width: 120 },
            ],
            rows: [
              {
                id: 'r1',
                candidate: 'Jordan Lee',
                candidateName: 'Jordan Lee',
                role: 'Senior backend',
                roleApplied: 'Senior Backend Engineer',
                bucket: 'pass',
                screeningBucket: 'pass',
                score: 82,
                screeningScore: 82,
                nextStep: 'scorecard',
              },
              {
                id: 'r2',
                candidate: 'Sam Rivera',
                candidateName: 'Sam Rivera',
                role: 'Platform eng',
                roleApplied: 'Platform Engineer',
                bucket: 'hold',
                screeningBucket: 'hold',
                score: 61,
                screeningScore: 61,
                nextStep: 'review',
              },
            ],
          },
        },
        left_dimensions: {
          table_data: {
            columns: [
              { field: 'dimension', headerName: 'Dimension', width: 180 },
              { field: 'score', headerName: 'Score', width: 80 },
              { field: 'evidence', headerName: 'Evidence', width: 280 },
            ],
            rows: [
              {
                id: 'd1',
                dimension: 'Technical depth',
                score: '5/5',
                evidence: 'Strong system design on idempotency and failure modes.',
              },
              {
                id: 'd2',
                dimension: 'Communication',
                score: '4/5',
                evidence: 'Clear trade-off discussion; minor rambling on edge cases.',
              },
            ],
          },
        },
        right_detail: {
          rich_text: {
            content: '## Screening snapshot\n\n**Jordan Lee** · **pass** · score **82**\n\n- Backend & distributed systems match role summary.\n- Sponsorship: not required.\n\n**Next:** switch to **Scorecard** and paste interview notes, or click a pass row to hand off.',
          },
        },
        right_summary: {
          rich_text: {
            content: 'Jordan Lee demonstrated senior-level backend depth in the payments reliability discussion. Communication was clear and structured. Recommend **hire** for Senior Backend Engineer with standard onboarding on front-end collaboration norms.',
          },
        },
      },
    },
  },
  panels: [
    {
      id: 'left_queue',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      config: { interviewPhase: 'screening' },
      source: {
        outputPath: 'result.screening.queue',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.screening.queue.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.screening.queue.columns', required: false },
        ],
      },
    },
    {
      id: 'left_dimensions',
      type: 'component',
      componentType: 'table',
      position: 'left',
      order: 0,
      outputKey: 'table_data',
      config: { interviewPhase: 'scorecard' },
      source: {
        outputPath: 'result.scorecard.table',
        bindings: [
          { targetKey: 'rows', sourcePath: 'result.scorecard.table.rows', required: true },
          { targetKey: 'columns', sourcePath: 'result.scorecard.table.columns', required: false },
        ],
      },
    },
    { id: 'center_chat', type: 'chat', position: 'center', order: 0, fixed: true },
    {
      id: 'right_detail',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      config: { interviewPhase: 'screening' },
      source: {
        outputPath: 'result.screening.detail',
        bindings: [{ targetKey: 'content', sourcePath: 'result.screening.detail.content', required: true }],
      },
    },
    {
      id: 'right_summary',
      type: 'component',
      componentType: 'rich_text',
      position: 'right',
      order: 0,
      outputKey: 'rich_text',
      config: { interviewPhase: 'scorecard' },
      source: {
        outputPath: 'result.summary',
        bindings: [{ targetKey: 'content', sourcePath: 'result.summary', required: true }],
      },
    },
  ],
};
